import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  alpha,
  useTheme,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs from "dayjs";
import {
  Plus,
  Play,
  Pause,
  Calendar,
  Trash2,
  Eye,
  Send,
  RotateCcw,
  AlertTriangle,
  Clock,
  PenLine,
} from "lucide-react";
import {
  fetchMarketingCampaigns,
  createMarketingCampaign,
  updateMarketingCampaign,
  deleteMarketingCampaign,
  sendMarketingCampaignNow,
  pauseMarketingCampaign,
  resumeMarketingCampaign,
  scheduleMarketingCampaign,
  fetchMarketingSegments,
} from "../../../../../../redux/actions/marketingAction";
import { useNavigate } from "react-router-dom";
import { backendClient } from "../../../../../../api/backend";
import DeleteConfirmDialog from "../DeleteConfirmDialog";

const STATUS_COLORS = {
  draft: "default",
  scheduled: "info",
  sending: "warning",
  paused: "secondary",
  sent: "primary",
  completed: "success",
};

const CampaignList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { campaigns, segments, loading } = useSelector((state) => state.marketing);
  const [page, setPage] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    channel: "email",
    segment_ids: [],
    sender_email: "",
    sender_name: "",
  });
  const [scheduleDate, setScheduleDate] = useState(dayjs());
  const [scheduleTime, setScheduleTime] = useState("12:00");
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // "send" or "schedule"
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null); // { valid, recipient_count, errors }

  useEffect(() => {
    dispatch(fetchMarketingCampaigns({ page: page + 1 }));
    dispatch(fetchMarketingSegments());
  }, [dispatch, page]);

  const handleValidateAndConfirm = async (campaign, action) => {
    setSelectedCampaign(campaign);
    setConfirmAction(action);
    setValidating(true);
    setValidation(null);
    setConfirmOpen(true);
    try {
      const result = await backendClient.validateMarketingCampaign(campaign.id);
      setValidation(result);
    } catch (e) {
      setValidation({ valid: false, recipient_count: 0, errors: [e.message || "Validation failed"] });
    } finally {
      setValidating(false);
    }
  };

  const handleConfirmSend = async () => {
    if (!selectedCampaign) return;
    setSubmitting(true);
    try {
      if (confirmAction === "schedule") {
        if (!scheduleDate || !scheduleTime) return;
        const [hours, minutes] = scheduleTime.split(":");
        const combinedDate = scheduleDate.hour(parseInt(hours)).minute(parseInt(minutes)).second(0).toISOString();
        await dispatch(scheduleMarketingCampaign(selectedCampaign.id, combinedDate));
      } else {
        await dispatch(sendMarketingCampaignNow(selectedCampaign.id));
      }
      setConfirmOpen(false);
      setScheduleOpen(false);
      dispatch(fetchMarketingCampaigns({ page: page + 1 }));
    } catch (e) {
      setValidation({ valid: false, recipient_count: 0, errors: [e.message || "Operation failed"] });
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name || "",
      description: campaign.description || "",
      channel: campaign.channel || "email",
      segment_ids: (campaign.segments || []).map((s) => s.id),
      sender_email: campaign.sender_email || "",
      sender_name: campaign.sender_name || "",
    });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      if (editingCampaign) {
        await dispatch(updateMarketingCampaign(editingCampaign.id, formData));
      } else {
        await dispatch(createMarketingCampaign(formData));
      }
      setCreateOpen(false);
      setEditingCampaign(null);
      setFormData({ name: "", description: "", channel: "email", segment_ids: [], sender_email: "", sender_name: "" });
      dispatch(fetchMarketingCampaigns({ page: page + 1 }));
    } finally {
      setSubmitting(false);
    }
  };

  // handleSchedule removed — merged into handleConfirmSend

  const items = campaigns?.items || [];
  const total = campaigns?.total || 0;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {t('mkt_campaigns')} ({total})
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => setCreateOpen(true)}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            bgcolor: theme.palette.primary.main,
          }}
        >
          {t('mkt_new_campaign')}
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, overflow: "hidden", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('common_name')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_channel')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_status')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_recipients')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_sent')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_opened')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_clicked')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_segments')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: theme.palette.text.secondary }}>
                    {t('mkt_no_campaigns')}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((campaign) => (
                  <TableRow
                    key={campaign.id}
                    hover
                    onClick={() => navigate(`/admin/marketing/campaigns/${campaign.id}`)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {campaign.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={campaign.channel} size="small" variant="outlined" sx={{ borderRadius: "6px", fontSize: "0.7rem" }} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={campaign.status}
                        size="small"
                        color={STATUS_COLORS[campaign.status] || "default"}
                        sx={{ borderRadius: "6px", fontSize: "0.7rem", fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>{campaign.total_recipients?.toLocaleString()}</TableCell>
                    <TableCell>{campaign.total_sent?.toLocaleString()}</TableCell>
                    <TableCell>{campaign.total_opened?.toLocaleString()}</TableCell>
                    <TableCell>{campaign.total_clicked?.toLocaleString()}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {(campaign.segments || []).map((seg) => (
                          <Chip
                            key={seg.id}
                            label={seg.name}
                            size="small"
                            sx={{
                              borderRadius: "6px",
                              fontSize: "0.65rem",
                              height: 20,
                              bgcolor: seg.color ? alpha(seg.color, 0.1) : alpha(theme.palette.primary.main, 0.1),
                              color: seg.color || theme.palette.primary.main,
                            }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        {["draft", "paused"].includes(campaign.status) && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenEdit(campaign)}>
                              <PenLine size={14} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {campaign.status === "draft" && (
                          <>
                            <Tooltip title="Send Now">
                              <IconButton size="small" onClick={() => handleValidateAndConfirm(campaign, "send")}>
                                <Send size={14} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Schedule">
                              <IconButton size="small" onClick={() => { handleValidateAndConfirm(campaign, "schedule"); setScheduleOpen(true); }}>
                                <Calendar size={14} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {campaign.status === "sending" && (
                          <Tooltip title="Pause">
                            <IconButton size="small" onClick={() => dispatch(pauseMarketingCampaign(campaign.id)).then(() => dispatch(fetchMarketingCampaigns({ page: page + 1 })))}>
                              <Pause size={14} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {campaign.status === "paused" && (
                          <Tooltip title="Resume">
                            <IconButton size="small" onClick={() => dispatch(resumeMarketingCampaign(campaign.id)).then(() => dispatch(fetchMarketingCampaigns({ page: page + 1 })))}>
                              <RotateCcw size={14} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {["draft", "completed"].includes(campaign.status) && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteTarget(campaign)}
                            >
                              <Trash2 size={14} color={theme.palette.error?.main} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {total > 25 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
          />
        )}
      </Paper>

      {/* Create Campaign Dialog */}
      <Dialog open={createOpen} onClose={() => { setCreateOpen(false); setEditingCampaign(null); setFormData({ name: "", description: "", channel: "email", segment_ids: [], sender_email: "", sender_name: "" }); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{editingCampaign ? t('mkt_edit_campaign') : t('mkt_new_campaign')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label={t('mkt_campaign_name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              size="small"
              required
            />
            <TextField
              label={t('mkt_description')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>{t('mkt_channel')}</InputLabel>
              <Select value={formData.channel} onChange={(e) => setFormData({ ...formData, channel: e.target.value })} label={t('mkt_channel')}>
                <MenuItem value="email">{t('mkt_email')}</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('mkt_target_segments')}</InputLabel>
              <Select
                multiple
                value={formData.segment_ids}
                onChange={(e) => setFormData({ ...formData, segment_ids: e.target.value })}
                label={t('mkt_target_segments')}
                renderValue={(selected) =>
                  selected.map((id) => {
                    const seg = (segments || []).find((s) => s.id === id);
                    return seg?.name || id;
                  }).join(", ")
                }
              >
                {(segments || []).map((seg) => (
                  <MenuItem key={seg.id} value={seg.id}>
                    <Checkbox checked={formData.segment_ids.indexOf(seg.id) > -1} size="small" />
                    <ListItemText primary={seg.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('mkt_sender_email')}
              value={formData.sender_email}
              onChange={(e) => setFormData({ ...formData, sender_email: e.target.value })}
              fullWidth
              size="small"
              placeholder="marketing@lawis.ai"
            />
            <TextField
              label={t('mkt_sender_name')}
              value={formData.sender_name}
              onChange={(e) => setFormData({ ...formData, sender_name: e.target.value })}
              fullWidth
              size="small"
              placeholder="Lawis"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setCreateOpen(false); setEditingCampaign(null); setFormData({ name: "", description: "", channel: "email", segment_ids: [], sender_email: "", sender_name: "" }); }}>{t('common_cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!formData.name || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
            {submitting ? (editingCampaign ? t('mkt_saving') : t('mkt_creating')) : (editingCampaign ? t('mkt_save') : t('mkt_create'))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation / Validation Dialog */}
      <Dialog open={confirmOpen} onClose={() => { setConfirmOpen(false); setScheduleOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {confirmAction === "schedule" ? t('mkt_schedule_campaign') : t('mkt_send_campaign_now')}
        </DialogTitle>
        <DialogContent>
          {validating ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3, justifyContent: "center" }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {t('mkt_validating')}
              </Typography>
            </Box>
          ) : validation && !validation.valid ? (
            <Box sx={{ py: 1 }}>
              <Box sx={{
                display: "flex", alignItems: "center", gap: 1, mb: 2,
                p: 1.5, borderRadius: "8px",
                bgcolor: alpha(theme.palette.error?.main || "#ef4444", 0.08),
                border: `1px solid ${alpha(theme.palette.error?.main || "#ef4444", 0.2)}`,
              }}>
                <AlertTriangle size={18} color={theme.palette.error?.main || "#ef4444"} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.error?.main }}>
                  {t('mkt_not_ready')}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {(validation.errors || []).map((err, i) => (
                  <Typography key={i} variant="body2" sx={{ color: theme.palette.text.secondary, pl: 1, borderLeft: `2px solid ${theme.palette.error?.main || "#ef4444"}` }}>
                    {err}
                  </Typography>
                ))}
              </Box>
            </Box>
          ) : validation && validation.valid ? (
            <Box sx={{ py: 1 }}>
              <Box sx={{
                display: "flex", alignItems: "center", gap: 1, mb: 2,
                p: 1.5, borderRadius: "8px",
                bgcolor: alpha("#10b981", 0.08),
                border: `1px solid ${alpha("#10b981", 0.2)}`,
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#10b981" }}>
                  {confirmAction === "schedule" ? t('mkt_ready_schedule') : t('mkt_ready_send')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                {t('mkt_campaign_label')} <strong>{selectedCampaign?.name}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                {t('mkt_recipients_label')} <strong>{validation.recipient_count.toLocaleString()}</strong> {t('mkt_contacts_unit')}
              </Typography>

              {confirmAction === "schedule" && (
                <Box sx={{ mt: 1 }}>
                  <DateCalendar
                    value={scheduleDate}
                    onChange={(newDate) => setScheduleDate(newDate)}
                    disablePast
                    sx={{
                      width: "100%",
                      "& .MuiPickersCalendarHeader-root": { px: 1 },
                    }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1, px: 1 }}>
                    <Clock size={18} color={theme.palette.text.secondary} />
                    <TextField
                      label={t('mkt_time')}
                      value={scheduleTime}
                      onChange={(e) => {
                        let v = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
                        if (v.length >= 3) v = v.slice(0, 2) + ":" + v.slice(2);
                        setScheduleTime(v);
                      }}
                      onBlur={() => {
                        const match = scheduleTime.match(/^(\d{1,2}):?(\d{2})$/);
                        if (match) {
                          const h = Math.min(23, parseInt(match[1]));
                          const m = Math.min(59, parseInt(match[2]));
                          setScheduleTime(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
                        } else if (scheduleTime) {
                          setScheduleTime("12:00");
                        }
                      }}
                      placeholder="HH:MM"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: 5 }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setConfirmOpen(false); setScheduleOpen(false); }}>{t('common_cancel')}</Button>
          {validation?.valid && (
            <Button
              variant="contained"
              onClick={handleConfirmSend}
              disabled={submitting || (confirmAction === "schedule" && (!scheduleDate || !scheduleTime))}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
              color={confirmAction === "send" ? "primary" : "primary"}
            >
              {submitting
                ? (confirmAction === "schedule" ? t('mkt_scheduling') : t('mkt_sending'))
                : (confirmAction === "schedule" ? t('mkt_schedule') : `${t('mkt_send_to')}${validation?.recipient_count || 0} ${t('mkt_contacts_unit')}`)}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await dispatch(deleteMarketingCampaign(deleteTarget.id));
          setDeleteTarget(null);
        }}
        itemName={deleteTarget?.name}
        itemType={t("mkt_campaign")}
      />
    </Box>
  );
};

export default CampaignList;
